import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, X } from "lucide-react";

import { useTargetableDevices } from "@/hooks/useAlarmRules";
import {
  RULE_TYPES_WITHOUT_DATAPOINT,
  VALID_RULE_TYPES,
  type RuleType,
} from "@/data/alarmRules/engineConstants";
import {
  alarmRuleSchema,
  generateRuleId,
  generateTemplateId,
  type AlarmRule,
} from "@/data/alarmRules/wireFormat";
import type { AlarmTargetableDevice } from "@/data/adapter";
import type { RuleDraft } from "@/data/alarmRules/templates";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/Button";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";

import { ActiveWhenEditor } from "./ActiveWhenEditor";
import { BehaviourPanel } from "./BehaviourPanel";
import { DatapointPicker } from "./DatapointPicker";
import { DeviceTargetingFields } from "./DeviceTargetingFields";
import { RecipientsPanel } from "./RecipientsPanel";
import { RuleTypeFields } from "./RuleTypeFields";
import { RULE_TYPE_LABEL_KEYS } from "./RuleList";
import { SeverityPicker } from "./SeverityPicker";

type EditorMode =
  | { kind: "create"; draft: RuleDraft; targets: AlarmTargetableDevice[] }
  | { kind: "edit"; rule: AlarmRule; deviceId: string };

interface Props {
  mode: EditorMode;
  candidateChildrenByDevice: Record<string, { id: string; name: string }[]>;
  onClose: () => void;
  onSave: (params: {
    targets: string[];
    rule: AlarmRule;
    templateId: string | undefined;
  }) => Promise<void>;
}

function ruleFromDraft(draft: RuleDraft, sourceDeviceName: string): AlarmRule {
  const now = Date.now();
  // The zod schema strips unknown keys; we don't keep template-only
  // annotations on the saved rule.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __draft, ...rest } = draft as RuleDraft & Record<string, unknown>;
  void sourceDeviceName;
  return {
    ...(rest as unknown as AlarmRule),
    id: generateRuleId(),
    template_id: generateTemplateId(),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Side drawer that authors a single rule (one logical edit). On save,
 * the rule is replicated to every selected target device — each
 * replica gets a unique `id` but shares the same `template_id` so the
 * list view can group replicas.
 */
export function RuleEditor({
  mode,
  candidateChildrenByDevice,
  onClose,
  onSave,
}: Props) {
  const { data: allTargets } = useTargetableDevices();
  const t = useT();

  const initialRule = useMemo<AlarmRule>(
    () =>
      mode.kind === "create"
        ? ruleFromDraft(mode.draft, mode.targets[0]?.name ?? "")
        : mode.rule,
    [mode],
  );
  const initialTargets =
    mode.kind === "create" ? mode.targets.map((d) => d.id) : [mode.deviceId];

  const [targets, setTargets] = useState<string[]>(initialTargets);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);

  const methods = useForm<AlarmRule>({
    resolver: zodResolver(alarmRuleSchema),
    defaultValues: initialRule,
    mode: "onBlur",
  });

  const { control, register, handleSubmit, watch, reset, formState } = methods;
  const type = watch("type");
  const severity = watch("severity");
  const deliveryMethod = watch("deliveryMethod");
  // Read isDirty during render so RHF's formState proxy subscribes to it
  // (reading it only inside an effect callback would not be tracked).
  const isDirty = formState.isDirty;

  // ─── Unsaved-edit protection (PERS-02, item 3.5f) ───────────────────
  // Layer 3 of the persistence rule: in-progress edits are mirrored to
  // sessionStorage so a page navigation or TB-iframe re-mount doesn't
  // destroy them. Keyed by rule id in edit mode (stable across re-opens);
  // create mode keys by template type+name because ruleFromDraft mints a
  // fresh id per open. Cleared on save and on explicit Cancel/close —
  // only an unmount-without-action leaves a draft behind to restore.
  const draftKey =
    mode.kind === "edit"
      ? `ems-rule-draft:${mode.rule.id}`
      : `ems-rule-draft:new:${mode.draft.type}:${mode.draft.name ?? ""}`;

  const clearStoredDraft = () => {
    try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  useEffect(() => {
    let stored: { values?: AlarmRule; targets?: unknown; savedAt?: number } | null = null;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) stored = JSON.parse(raw);
    } catch { /* storage unavailable — fall through to a clean form */ }
    if (stored?.values && typeof stored.values === "object") {
      // keepDefaultValues keeps initialRule as the pristine baseline so
      // isDirty stays true and the mirror effect below keeps running;
      // the zod resolver re-validates everything on submit regardless.
      reset(stored.values, { keepDefaultValues: true });
      if (Array.isArray(stored.targets) && stored.targets.every((t) => typeof t === "string")) {
        setTargets(stored.targets as string[]);
      }
      setRestoredFromDraft(true);
    } else {
      reset(initialRule);
    }
    // draftKey is derived from `mode`, same as initialRule — one run per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRule, reset]);

  useEffect(() => {
    const targetsDirty = targets.join(",") !== initialTargets.join(",");
    if (!isDirty && !targetsDirty) return;
    const write = (values: unknown) => {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify({ values, targets, savedAt: Date.now() }));
      } catch { /* ignore — protection degrades gracefully */ }
    };
    // Write immediately (covers target-only changes; watch only fires on
    // field changes), then keep mirroring as the operator types.
    write(methods.getValues());
    const sub = watch((values) => write(values));
    return () => sub.unsubscribe();
    // initialTargets is rebuilt each render from the stable `mode` prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, watch, draftKey, targets, methods]);

  // Composite rules pull child candidates from the first selected target.
  const primaryDeviceId = targets[0] ?? null;
  const candidates = primaryDeviceId
    ? (candidateChildrenByDevice[primaryDeviceId] ?? []).filter(
        (c) => c.id !== initialRule.id,
      )
    : [];

  const needsEmailConfirmation =
    severity === "CRITICAL" && deliveryMethod === "email";

  const submit = handleSubmit(async (values) => {
    setSaveError(null);
    if (needsEmailConfirmation && !emailConfirmed) {
      setSaveError(t("manage.rules.editor.confirmEmail"));
      return;
    }
    if (targets.length === 0) {
      setSaveError(t("manage.rules.editor.selectOne"));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        targets,
        rule: values,
        templateId: values.template_id ?? generateTemplateId(),
      });
      clearStoredDraft();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t("manage.rules.editor.saveFailedShort"));
    } finally {
      setSaving(false);
    }
  });

  // N11 — "Save to N" is the highest fan-out, least-guarded write in the
  // app: it replicates the rule to every selected device. Gate it behind a
  // hold (the same grammar as the schedule page save and rule delete). The
  // hold escalates from warn → destructive when the blast radius is large
  // (more than one target) OR the rule is CRITICAL severity.
  const saveIntent: "warn" | "destructive" =
    targets.length > 1 || severity === "CRITICAL" ? "destructive" : "warn";
  // The hold's onConfirm reuses the exact same submit handler — same
  // validation, same success/failure echo, just guarded by the gesture.
  const holdSave = () => { void submit(); };

  // Explicit dismissal (X / Cancel) is the deliberate "drop my edits"
  // action — it also drops the sessionStorage mirror. Unmount via page
  // navigation goes through onClose-less teardown and keeps the draft.
  const handleCancel = () => {
    clearStoredDraft();
    onClose();
  };

  const allowedKinds: AlarmTargetableDevice["kind"][] | undefined =
    type === "comm_degradation" || type === "inactivity"
      ? undefined // every device can have a comm-loss alarm
      : type === "peak_demand" || type === "idle_consumption"
        ? ["grid"]
        : undefined;

  // Portal to <body> so the drawer is never trapped by an ancestor's
  // containing block (the route-reveal entrance animation leaves a transform on
  // the page-content wrapper, which would otherwise scope this `fixed` drawer to
  // the page-content box instead of the viewport — compressing it when the
  // content is short). React context (FormProvider) still flows through portals.
  return (
    <FormProvider {...methods}>
      {createPortal(
        <aside
          className="fixed inset-y-0 right-0 w-[min(560px,100vw)] z-40 bg-surface border-l border-edge shadow-2xl flex flex-col"
          aria-label={t("manage.rules.editor.ariaEditor")}
        >
        <div className="flex items-center justify-between border-b border-edge px-4 py-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-faint">
              {mode.kind === "create" ? t("manage.rules.editor.newRule") : t("manage.rules.editor.editRule")}
            </div>
            <div className="text-[13px] font-semibold truncate">
              {watch("name") || t("manage.rules.editor.unnamed")}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="text-text-faint hover:text-text"
            aria-label={t("manage.rules.editor.ariaClose")}
          >
            <X className="size-4" />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {/* Restored-draft notice (item 3.5f) */}
          {restoredFromDraft && (
            <div className="border border-warn/40 bg-warn/10 text-warn rounded p-2 text-[11px] flex items-center justify-between gap-2">
              <span>{t("manage.rules.editor.restored")}</span>
              <button
                type="button"
                onClick={() => {
                  clearStoredDraft();
                  reset(initialRule);
                  setTargets(initialTargets);
                  setRestoredFromDraft(false);
                }}
                className="rounded border border-warn/40 px-2 py-0.5 uppercase tracking-wide hover:bg-warn/20"
              >
                {t("manage.rules.editor.reset")}
              </button>
            </div>
          )}

          {/* Identity */}
          <section className="space-y-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider text-text-faint">
                {t("manage.rules.editor.name")}
              </span>
              <input
                type="text"
                className="form-input"
                {...register("name")}
                autoFocus
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider text-text-faint">
                {t("manage.rules.editor.type")}
              </span>
              {/* register("type") wire VALUES stay raw; only the visible
                  option label is translated (RULE_TYPE_LABEL_KEYS). */}
              <select className="form-input" {...register("type")}>
                {VALID_RULE_TYPES.map((rt) => (
                  <option key={rt} value={rt}>
                    {t(RULE_TYPE_LABEL_KEYS[rt])}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wider text-text-faint">
                  {t("manage.rules.editor.severity")}
                </span>
                <Controller
                  control={control}
                  name="severity"
                  render={({ field }) => (
                    <SeverityPicker value={field.value} onChange={field.onChange} />
                  )}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wider text-text-faint">
                  {t("manage.rules.editor.delivery")}
                </span>
                {/* deliveryMethod wire VALUES (notification/email) stay raw. */}
                <select className="form-input" {...register("deliveryMethod")}>
                  <option value="notification">notification</option>
                  <option value="email">email</option>
                </select>
              </label>
            </div>

            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" {...register("enabled")} />
              {t("manage.rules.editor.enabled")}
            </label>
          </section>

          {/* Datapoint */}
          {!RULE_TYPES_WITHOUT_DATAPOINT.has(type as RuleType) && (
            <section>
              <div className="text-[11px] uppercase tracking-wider text-text-faint mb-1">
                {t("manage.rules.editor.datapoint")}
              </div>
              <Controller
                control={control}
                name="datapoint"
                render={({ field }) => (
                  <DatapointPicker
                    deviceId={primaryDeviceId}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </section>
          )}

          {/* Per-type fields */}
          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-text-faint">
              {t("manage.rules.editor.configuration", { type: t(RULE_TYPE_LABEL_KEYS[type as RuleType]) })}
            </div>
            <RuleTypeFields
              type={type as RuleType}
              deviceId={primaryDeviceId}
              candidateChildren={candidates}
            />
          </section>

          {/* Active-when gate */}
          <section>
            <Controller
              control={control}
              name="active_when"
              render={({ field }) => (
                <ActiveWhenEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </section>

          {/* v4 Behaviour panel — auto_clear + propagate. The
              notify_on_clear toggle lives in the Recipients panel
              because it only takes effect when there are recipients. */}
          <section>
            <Controller
              control={control}
              name="auto_clear"
              render={({ field: autoClear }) => (
                <Controller
                  control={control}
                  name="propagate"
                  render={({ field: propagate }) => (
                    <BehaviourPanel
                      autoClear={autoClear.value}
                      onAutoClearChange={autoClear.onChange}
                      propagate={propagate.value}
                      onPropagateChange={propagate.onChange}
                    />
                  )}
                />
              )}
            />
          </section>

          {/* v4 Recipients panel — inline email lists + escalation +
              notify_on_clear. The cloud SPA never references EMS-Admin's
              local recipient groups; operators paste real email addresses. */}
          <section>
            <Controller
              control={control}
              name="notification"
              render={({ field }) => (
                <RecipientsPanel
                  value={field.value ?? null}
                  onChange={field.onChange}
                />
              )}
            />
          </section>

          {/* Targets */}
          <section>
            <DeviceTargetingFields
              selectedIds={targets}
              onChange={setTargets}
              allowedKinds={allowedKinds}
            />
            {mode.kind === "edit" && targets.length > 1 && (
              <p className="text-[11px] text-text-faint mt-1">
                {t("manage.rules.editor.replicaNote")}
              </p>
            )}
          </section>

          {/* Alarm message */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-text-faint">
              {t("manage.rules.editor.alarmMessage")}
            </span>
            <input
              type="text"
              className="form-input"
              {...register("alarm_message")}
            />
          </label>

          {/* High-blast-radius callout */}
          {needsEmailConfirmation && (
            <div className="border border-warn/40 bg-warn/10 text-warn rounded p-3 text-[12px] flex items-start gap-2">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              <div>
                <strong>{t("manage.rules.editor.criticalEmail.title")}</strong>{" "}
                {t("manage.rules.editor.criticalEmail.body")}
                <label className="mt-2 flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={emailConfirmed}
                    onChange={(e) => setEmailConfirmed(e.target.checked)}
                  />
                  {t("manage.rules.editor.criticalEmail.confirm")}
                </label>
              </div>
            </div>
          )}

          {/* Errors */}
          {formState.errors && Object.keys(formState.errors).length > 0 && (
            <div className="border border-critical/40 bg-critical/10 text-critical rounded p-3 text-[11px] space-y-1">
              <strong className="block">{t("manage.rules.editor.fixFields")}</strong>
              {Object.entries(formState.errors).map(([k, v]) => (
                <div key={k}>
                  <code>{k}</code>: {(v as { message?: string })?.message ?? "invalid"}
                </div>
              ))}
            </div>
          )}
          {saveError && (
            <div className="border border-critical/40 bg-critical/10 text-critical rounded p-3 text-[12px]">
              {saveError}
            </div>
          )}
        </form>

        <div className="border-t border-edge px-4 py-3 flex items-center justify-between gap-2">
          <div className="text-[11px] text-text-faint">
            {targets.length === 0
              ? t("manage.rules.editor.pickAtLeastOne")
              : targets.length === 1
                ? t("manage.rules.editor.willWrite.one")
                : t("manage.rules.editor.willWrite.many", { count: targets.length })}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              size="sm"
              onClick={handleCancel}
              className="uppercase tracking-wide"
            >
              {t("common.cancel")}
            </Button>
            {/* N11 — the highest fan-out write in the app is now hold-gated.
                Compact h-7 sizing (matching the schedule/controller call
                sites) so the 560px drawer footer doesn't grow on the narrow
                TB iframe. The hold reuses the existing submit handler, so
                its success/failure echo is unchanged — just guarded. When
                no device is selected the plain Cancel stays but the hold is
                replaced by a disabled stub so the operator can't arm it. */}
            {saving || targets.length === 0 ? (
              <span className="inline-flex h-7 select-none items-center rounded-md border border-edge bg-surface px-3 text-[11px] font-semibold uppercase tracking-wide text-text-faint opacity-60">
                {saving
                  ? t("manage.rules.editor.saving")
                  : targets.length === 1
                    ? t("manage.rules.editor.saveTo.one")
                    : t("manage.rules.editor.saveTo.many", { count: targets.length })}
              </span>
            ) : (
              <HoldToConfirm
                label={
                  targets.length === 1
                    ? t("manage.rules.editor.holdSave.one")
                    : t("manage.rules.editor.holdSave.many", { count: targets.length })
                }
                intent={saveIntent}
                onConfirm={holdSave}
                className="h-7 px-2 text-[11px] normal-case tracking-normal"
              />
            )}
          </div>
        </div>
        {/* Suppress unused-var lint for allTargets — we depend on the
            query for cache prewarming. */}
        <span className="sr-only" data-targets={allTargets?.length ?? 0} />
        </aside>,
        document.body,
      )}
    </FormProvider>
  );
}
